'use client';

import JSZip from 'jszip';
import { STUDIO_DOCUMENTS, createStudioPage, createStudioProject, type StudioProject, type StudioTemplate } from './project';
import { getBrowserMediaList } from './browser-media';
import { convertLegacyHeaderToBlocks, isLegacyHeaderConfig } from '@/core/components/Builder/legacyHeaderConverter';
import { convertLegacyFooterToBlocks, isLegacyFooterConfig } from '@/core/components/Builder/legacyFooterConverter';

function json(value: unknown): string {
  return `${JSON.stringify(value, null, 2)}\n`;
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export async function downloadStudioProjectZip(project: StudioProject): Promise<void> {
  const zip = new JSZip();

  zip.file('theme.meta.json', json({
    schemaVersion: 1,
    name: project.name,
    slug: project.slug,
    industry: project.industry,
    siteMode: project.siteMode || 'shop',
    template: project.template || 'shop',
    styleDescription: project.description,
    status: 'draft',
    palette: project.palette,
    paletteRoles: {
      C1: 'primary / akcent CTA',
      C2: 'tekst / dark neutral',
      C3: 'secondary / support',
      C4: 'highlight / badge',
      C5: 'background / light neutral',
    },
    fonts: project.fonts,
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
    generator: 'ShopStag Theme Studio',
  }));

  const header = project.documents.header?.content || [];
  const footer = project.documents.footer?.content || [];
  zip.file('header_config.json', json({ schemaVersion: 3, kind: 'header', content: header, assets: [] }));
  zip.file('footer_config.json', json({ schemaVersion: 3, kind: 'footer', content: footer, assets: [] }));
  zip.file('site_config.json', json({
    schemaVersion: 3,
    global_palette: project.palette,
    global_font: project.fonts.body,
    header_layout: { schemaVersion: 3, kind: 'header', content: header, assets: [] },
    footer_layout: { schemaVersion: 3, kind: 'footer', content: footer, assets: [] },
  }));
  zip.file('cookie_config.json', json(project.cookieConfig || {
    enabled: true,
    position: 'bottom',
    title: 'Dbamy o Twoją prywatność',
    description: 'Używamy plików cookies, aby sklep działał prawidłowo i był wygodniejszy.',
    accept_text: 'Akceptuję',
    reject_text: 'Tylko niezbędne',
  }));

  const files: Array<{ id: string; path: string; kind: string; slug: string }> = [];
  for (const definition of STUDIO_DOCUMENTS) {
    if (definition.id === 'header' || definition.id === 'footer') continue;
    const page = project.documents[definition.id];
    if (!page) continue;
    const isTemplate = definition.exportPath.startsWith('templates/');
    zip.file(definition.exportPath, json({
      ...(isTemplate ? { templateType: page.page_type } : { slug: page.slug }),
      meta: {
        title: page.meta_title || page.title,
        description: page.meta_description || '',
      },
      blocks: page.content || [],
    }));
    files.push({ id: definition.id, path: definition.exportPath, kind: isTemplate ? 'template' : 'page', slug: page.slug });
  }

  zip.file('manifest.json', json({
    schema: 'shopstag-theme-package',
    schemaVersion: 1,
    minimumStorefrontSchemaVersion: 3,
    theme: 'theme.meta.json',
    siteConfig: 'site_config.json',
    header: 'header_config.json',
    footer: 'footer_config.json',
    cookie: 'cookie_config.json',
    documents: files,
  }));

  // Include local browser media files in the assets/ folder
  try {
    const browserMedia = await getBrowserMediaList();
    if (browserMedia && browserMedia.length > 0) {
      for (const item of browserMedia) {
        if (item.path.startsWith('data:')) {
          const commaIdx = item.path.indexOf(',');
          if (commaIdx !== -1) {
            const base64Data = item.path.substring(commaIdx + 1);
            zip.file(`assets/${item.name}`, base64Data, { base64: true });
          }
        }
      }
    }
  } catch {
    // Ignore media packaging error
  }

  zip.file('README.txt', [
    `Motyw: ${project.name}`,
    '',
    'Paczka została wygenerowana przez ShopStag Theme Studio.',
    'Format: shopstag-theme-package v1 / Storefront schema v3.',
    'Plik manifest.json opisuje komplet stron i szablonów w paczce.',
  ].join('\n'));

  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
  downloadBlob(blob, `${project.slug}.zip`);
}

async function readJsonFile(zip: JSZip, path: string): Promise<any> {
  const entry = zip.file(path);
  if (!entry) throw new Error(`Brak wymaganego pliku ${path}.`);
  return JSON.parse(await entry.async('string'));
}

export async function importStudioProjectZip(file: File): Promise<StudioProject> {
  if (!file.name.toLowerCase().endsWith('.zip')) throw new Error('Wybierz paczkę ZIP motywu.');
  if (file.size > 25 * 1024 * 1024) throw new Error('Paczka ZIP przekracza limit 25 MB.');

  const zip = await JSZip.loadAsync(file);
  const manifest = await readJsonFile(zip, 'manifest.json');
  if (manifest?.schema !== 'shopstag-theme-package' || manifest?.schemaVersion !== 1) {
    throw new Error('Nieobsługiwany format paczki motywu.');
  }
  const meta = await readJsonFile(zip, manifest.theme || 'theme.meta.json');
  const template: StudioTemplate = ['service', 'school'].includes(meta?.template) ? meta.template : 'shop';
  const project = createStudioProject(meta?.name || file.name.replace(/\.zip$/i, ''), template);
  project.slug = meta?.slug || project.slug;
  project.industry = meta?.industry || 'other';
  project.description = meta?.styleDescription || '';
  project.palette = Array.isArray(meta?.palette) && meta.palette.length >= 5 ? meta.palette.slice(0, 5) : project.palette;
  project.fonts = meta?.fonts || project.fonts;
  if (meta?.siteMode === 'site' || meta?.siteMode === 'shop') project.siteMode = meta.siteMode;

  const headerEntry = zip.file(manifest.header || 'header_config.json');
  if (headerEntry) {
    const header = JSON.parse(await headerEntry.async('string'));
    project.documents.header = {
      ...project.documents.header,
      content: isLegacyHeaderConfig(header) ? convertLegacyHeaderToBlocks(header) : (header.content || []),
    };
  }
  const footerEntry = zip.file(manifest.footer || 'footer_config.json');
  if (footerEntry) {
    const footer = JSON.parse(await footerEntry.async('string'));
    project.documents.footer = {
      ...project.documents.footer,
      content: isLegacyFooterConfig(footer) ? convertLegacyFooterToBlocks(footer) : (footer.content || []),
    };
  }
  const cookieEntry = zip.file(manifest.cookie || 'cookie_config.json');
  if (cookieEntry) project.cookieConfig = JSON.parse(await cookieEntry.async('string'));

  for (const definition of STUDIO_DOCUMENTS) {
    if (definition.id === 'header' || definition.id === 'footer') continue;
    const entry = zip.file(definition.exportPath);
    if (!entry) continue;
    const payload = JSON.parse(await entry.async('string'));
    // A page the template doesn't have (e.g. a hand-edited ZIP) still comes in.
    const current = project.documents[definition.id] || createStudioPage(definition);
    project.documents[definition.id] = {
      ...current,
      title: payload?.meta?.title || current.title,
      meta_title: payload?.meta?.title || undefined,
      meta_description: payload?.meta?.description || undefined,
      content: Array.isArray(payload?.blocks) ? payload.blocks : [],
      updated_at: new Date().toISOString(),
    };
  }

  project.updatedAt = new Date().toISOString();
  return project;
}

const LOOSE_FILE_DOCUMENTS: Record<string, string> = {
  strona_glowna: 'home',
  homepage: 'home',
  home: 'home',
  karta_produktu: 'product',
  produkt: 'product',
  product_template: 'product',
  karta_kategorii: 'category',
  kategoria: 'category',
  category_template: 'category',
  koszyk: 'cart',
  cart: 'cart',
  checkout: 'checkout',
  zamowienie: 'checkout',
  logowanie: 'auth',
  auth: 'auth',
  konto: 'club',
  blog: 'blog',
  kontakt: 'contact',
  contact: 'contact',
  wynajem: 'rental',
  wynajem_maszyn: 'rental',
  o_nas: 'about',
  o_sklepie: 'about',
  info: 'about',
  dostawy: 'delivery',
  platnosci_i_dostawa: 'delivery',
  odstapienie_od_umowy: 'returns',
  zwroty_i_reklamacje: 'returns',
  reklamacje_i_zwroty: 'returns',
  serwis: 'service',
  faq: 'faq',
  regulamin: 'terms',
  terms: 'terms',
  polityka_prywatnosci: 'privacy',
  privacy: 'privacy',
  polityka_cookies: 'cookies',
  cookies: 'cookies',
  article: 'article',
  article1: 'article',
  artykul: 'article',
};

function normalizedFileStem(fileName: string): string {
  return fileName
    .replace(/\.json$/i, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function payloadBlocks(payload: any): any[] | null {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.blocks)) return payload.blocks;
  return null;
}

/**
 * Opens the older "folder full of JSON files" export as a single Studio project.
 * Unknown files are ignored, missing documents keep their production-safe defaults.
 */
export async function importStudioProjectFiles(files: File[]): Promise<{ project: StudioProject; imported: number; skipped: string[] }> {
  const jsonFiles = files.filter((file) => file.name.toLowerCase().endsWith('.json'));
  if (jsonFiles.length === 0) throw new Error('Wybierz pliki JSON wyeksportowane z edytora.');

  const project = createStudioProject('Zaimportowany motyw');
  const skipped: string[] = [];
  let imported = 0;

  for (const file of jsonFiles) {
    let payload: any;
    try {
      payload = JSON.parse(await file.text());
    } catch {
      skipped.push(file.name);
      continue;
    }
    const stem = normalizedFileStem(file.name);

    if (stem.includes('header_config') || isLegacyHeaderConfig(payload)) {
      project.documents.header = {
        ...project.documents.header,
        content: isLegacyHeaderConfig(payload) ? convertLegacyHeaderToBlocks(payload) : (payloadBlocks(payload) || project.documents.header.content),
      };
      imported += 1;
      continue;
    }
    if (stem.includes('footer_config') || isLegacyFooterConfig(payload)) {
      project.documents.footer = {
        ...project.documents.footer,
        content: isLegacyFooterConfig(payload) ? convertLegacyFooterToBlocks(payload) : (payloadBlocks(payload) || project.documents.footer.content),
      };
      imported += 1;
      continue;
    }
    if (stem.includes('cookie_config')) {
      project.cookieConfig = payload;
      imported += 1;
      continue;
    }

    const stemWithoutLegacyPrefix = stem.replace(/^i\d+_/, '');
    const documentId = LOOSE_FILE_DOCUMENTS[stem] || LOOSE_FILE_DOCUMENTS[stemWithoutLegacyPrefix];
    const blocks = payloadBlocks(payload);
    if (!documentId || !blocks) {
      skipped.push(file.name);
      continue;
    }
    const current = project.documents[documentId];
    project.documents[documentId] = {
      ...current,
      content: blocks,
      meta_title: payload?.meta?.title || current.meta_title,
      meta_description: payload?.meta?.description || current.meta_description,
      updated_at: new Date().toISOString(),
    };
    imported += 1;
  }

  if (imported === 0) throw new Error('Nie rozpoznano żadnego dokumentu motywu w wybranych plikach.');
  const directory = jsonFiles[0]?.webkitRelativePath?.split('/')[0];
  project.name = directory || 'Zaimportowany motyw';
  project.slug = project.name
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || project.slug;
  project.updatedAt = new Date().toISOString();
  return { project, imported, skipped };
}
